import { Button } from '@hologram-dimension/button';
import { Icon } from '@hologram-dimension/icon';
import type { ColorTokenNames } from '@hologram-dimension/tokens/colors';
import clsx from 'clsx/lite';
import { type FC, type ReactNode, useCallback, useState } from 'react';
import styles from './AppNotificationBanner.module.css';

interface AppNotification {
  type: 'danger' | 'warning';
  label?: string;
  children?: ReactNode;
  action?: string;
  href?: string;
  to?: string;
  dismissable?: boolean;
  onDismiss?: AnyFunction;
}

interface AppNotificationBannerProps {
  notifications: Array<AppNotification>;
}

const AppNotificationBanner: FC<AppNotificationBannerProps> = ({ notifications }) => {
  const [selectedNotification, setSelectedNotification] = useState(0);

  const {
    type,
    label = 'Warning',
    children,
    href,
    to,
    action,
    dismissable = false,
    onDismiss,
  } = notifications[selectedNotification];

  const rotateNotification = useCallback(() => {
    setSelectedNotification((prev) => (prev + 1 >= notifications.length ? 0 : prev + 1));
  }, [notifications.length]);

  const iconFill: ColorTokenNames | 'currentColor' =
    type === 'warning' ? 'DdsColorPaletteOrange50' : 'DdsColorPaletteRed86';

  const hasNav = notifications.length > 1 || dismissable;

  return (
    <div
      className={clsx(
        styles.banner,
        hasNav && styles.bannerGrid,
        !hasNav && styles.bannerFlex,
        styles[type]
      )}
    >
      {hasNav && <div className={styles.empty} />}
      <div className={styles.content}>
        <Icon
          className={styles.icon}
          name={type === 'warning' ? 'Warning' : 'Danger'}
          fill={iconFill}
        />
        <div className={styles.text}>
          <strong>{label}:</strong>&nbsp;<span>{children}</span>
          {(to || href) && action && (
            <>
              {' '}
              {href ? (
                <Button
                  className={styles.button}
                  href={href}
                  variant="tertiary"
                  target="_blank"
                  rel="noreferrer"
                >
                  {action}
                </Button>
              ) : (
                <Button className={styles.button} to={to!} variant="tertiary">
                  {action}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
      {hasNav && (
        <div className={styles.nav}>
          {dismissable && (
            <Button variant="tertiary" className={styles.button} onClick={onDismiss}>
              Dismiss
            </Button>
          )}
          {notifications.length > 1 && (
            <Button
              iconEnd={<Icon name="ChevronSingleEast" color={iconFill} />}
              variant="tertiary"
              className={styles.button}
              onClick={rotateNotification}
            >{`${selectedNotification + 1} of ${notifications.length}`}</Button>
          )}
        </div>
      )}
    </div>
  );
};

export default AppNotificationBanner;
